import { z } from 'zod';
import { OrgId, UserId, UnixTs } from '../types/branded';

export const PaymentModuleIdSchema = z.enum([
  'donations',
  'tip-a-teacher',
  'camps',
  'tuition',
  'enrollment',
]);
export type PaymentModuleId = z.infer<typeof PaymentModuleIdSchema>;

export const PaymentProviderSchema = z.enum(['stripe', 'sola', 'banquest']);
export type PaymentProvider = z.infer<typeof PaymentProviderSchema>;

export const DonationFundingMethodSchema = z.enum(['sola_daf', 'stripe_card']);
export type DonationFundingMethod = z.infer<typeof DonationFundingMethodSchema>;

export const PaymentEventIngestSourceSchema = z.enum([
  'initiation',
  'provider_poll',
  'provider_webhook',
  'operator_action',
]);
export type PaymentEventIngestSource = z.infer<typeof PaymentEventIngestSourceSchema>;

export const DonationPaymentInitiationKindSchema = z.enum([
  'hosted_checkout',
  'daf_recommendation',
  'offline_entry',
]);
export type DonationPaymentInitiationKind =
  z.infer<typeof DonationPaymentInitiationKindSchema>;

export const DonationSettlementStateSchema = z.enum([
  'pending',
  'settled',
  'voided',
  'failed',
  'refunded',
]);
export type DonationSettlementState = z.infer<typeof DonationSettlementStateSchema>;

export const DonationSettlementAuthoritySchema = z.enum([
  'synchronous_initiation',
  'provider_poll',
  'provider_webhook',
  'operator_action',
]);
export type DonationSettlementAuthority =
  z.infer<typeof DonationSettlementAuthoritySchema>;

export const DonationPaymentRecordSchema = z.object({
  provider: PaymentProviderSchema,
  fundingMethod: DonationFundingMethodSchema,
  initiationKind: DonationPaymentInitiationKindSchema,
  providerTransactionRef: z.string().min(1).nullable(),
  providerEventRef: z.string().min(1).nullable(),
  settlementState: DonationSettlementStateSchema,
  settlementAuthority: DonationSettlementAuthoritySchema,
  submittedAt: UnixTs.nullable(),
  settledAt: UnixTs.nullable(),
  lastSettlementCheckAt: UnixTs.nullable(),
  nextSettlementCheckAt: UnixTs.nullable(),
  lastSettlementNote: z.string().min(1).nullable(),
}).strict();
export type DonationPaymentRecord = z.infer<typeof DonationPaymentRecordSchema>;

const PaymentFundingMethodAvailabilityBaseSchema = z.object({
  method: DonationFundingMethodSchema,
  label: z.string().min(1),
  available: z.boolean(),
  reasonCode: z.string().min(1).nullable().optional(),
  reasonMessage: z.string().min(1).nullable().optional(),
}).strict();

export const PaymentAccountTypeSchema = z.enum(['express', 'standard', 'custom']);
export type PaymentAccountType = z.infer<typeof PaymentAccountTypeSchema>;

export const PaymentChargeStrategySchema = z.enum(['direct', 'destination']);
export type PaymentChargeStrategy = z.infer<typeof PaymentChargeStrategySchema>;

export const PaymentHolderTypeSchema = z.enum(['family', 'parent', 'teacher', 'school']);
export type PaymentHolderType = z.infer<typeof PaymentHolderTypeSchema>;

export const PaymentHolderStatusSchema = z.enum(['active', 'inactive', 'pending']);
export type PaymentHolderStatus = z.infer<typeof PaymentHolderStatusSchema>;

export const PaymentManagementLinkStatusSchema = z.enum(['linked', 'ambiguous', 'orphaned', 'stale']);
export type PaymentManagementLinkStatus = z.infer<typeof PaymentManagementLinkStatusSchema>;

export const PaymentManagementClaimStatusSchema = z.enum(['matched', 'missing', 'mismatch']);
export type PaymentManagementClaimStatus = z.infer<typeof PaymentManagementClaimStatusSchema>;

export const PaymentHostedFlowKindSchema = z.enum([
  'donation-checkout',
  'tip-checkout',
  'family-checkout',
  'billing-portal',
  'setup',
]);
export type PaymentHostedFlowKind = z.infer<typeof PaymentHostedFlowKindSchema>;

export const PaymentWalletPolicySchema = z.enum(['shared-school-parent']);
export type PaymentWalletPolicy = z.infer<typeof PaymentWalletPolicySchema>;

export const PaymentOnboardingStatusSchema = z.enum([
  'not_started',
  'pending',
  'restricted',
  'active',
]);
export type PaymentOnboardingStatus = z.infer<typeof PaymentOnboardingStatusSchema>;

export const PaymentCheckoutModeSchema = z.enum(['payment', 'subscription']);
export type PaymentCheckoutMode = z.infer<typeof PaymentCheckoutModeSchema>;

export const PaymentCheckoutOperationKeySchema = z.enum([
  'tuition_checkout',
  'enrollment_registration_checkout',
  'donations_checkout_one_time',
  'donations_checkout_recurring',
  'donations_parnes_hayom_checkout',
  'tip_a_teacher_checkout',
  'camps_registration_checkout',
  'camps_balance_checkout',
]);
export type PaymentCheckoutOperationKey = z.infer<typeof PaymentCheckoutOperationKeySchema>;

export const PaymentRecurringIntervalSchema = z.enum(['day', 'week', 'month', 'year']);
export type PaymentRecurringInterval = z.infer<typeof PaymentRecurringIntervalSchema>;

export const PaymentsAuthoritySchema = z.enum(['central-control-plane']);
export type PaymentsAuthority = z.infer<typeof PaymentsAuthoritySchema>;

export const PaymentTopologySchema = z.enum(['single-head-multi-sub-api']);
export type PaymentTopology = z.infer<typeof PaymentTopologySchema>;

export const PaymentWebhookTopologySchema = z.enum(['one-router']);
export type PaymentWebhookTopology = z.infer<typeof PaymentWebhookTopologySchema>;

export const PaymentMethodConfigurationSourceSchema = z.enum(['platform', 'school']);
export type PaymentMethodConfigurationSource =
  z.infer<typeof PaymentMethodConfigurationSourceSchema>;

export const PaymentMethodConfigurationIdSchema = z.string().trim().regex(/^pmc_/);
export type PaymentMethodConfigurationId = z.infer<typeof PaymentMethodConfigurationIdSchema>;

const PaymentMethodConfigurationCatalogEntryBaseSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  enabled: z.boolean(),
}).strict();

export const PaymentMethodConfigurationCatalogEntrySchema =
  PaymentMethodConfigurationCatalogEntryBaseSchema.extend({
    paymentMethodConfigurationId: PaymentMethodConfigurationIdSchema.nullable(),
    source: PaymentMethodConfigurationSourceSchema,
  }).strict();
export type PaymentMethodConfigurationCatalogEntry =
  z.infer<typeof PaymentMethodConfigurationCatalogEntrySchema>;

export const PlatformPaymentMethodConfigurationCatalogEntrySchema =
  PaymentMethodConfigurationCatalogEntryBaseSchema.extend({
    paymentMethodConfigurationId: PaymentMethodConfigurationIdSchema.nullable(),
    source: z.literal('platform'),
  }).strict();
export type PlatformPaymentMethodConfigurationCatalogEntry =
  z.infer<typeof PlatformPaymentMethodConfigurationCatalogEntrySchema>;

export const SchoolPaymentMethodConfigurationCatalogEntrySchema =
  PaymentMethodConfigurationCatalogEntryBaseSchema.extend({
    paymentMethodConfigurationId: PaymentMethodConfigurationIdSchema,
    source: z.literal('school'),
  }).strict();
export type SchoolPaymentMethodConfigurationCatalogEntry =
  z.infer<typeof SchoolPaymentMethodConfigurationCatalogEntrySchema>;

export const PaymentMethodConfigurationAssignmentSchema = z.object({
  operationKey: PaymentCheckoutOperationKeySchema,
  catalogEntryId: z.string().min(1).nullable(),
}).strict();
export type PaymentMethodConfigurationAssignment =
  z.infer<typeof PaymentMethodConfigurationAssignmentSchema>;

export const PaymentMethodConfigurationResolvedAssignmentSchema = z.object({
  operationKey: PaymentCheckoutOperationKeySchema,
  catalogEntryId: z.string().min(1).nullable(),
  resolvedCatalogEntry: PaymentMethodConfigurationCatalogEntrySchema.nullable(),
}).strict();
export type PaymentMethodConfigurationResolvedAssignment =
  z.infer<typeof PaymentMethodConfigurationResolvedAssignmentSchema>;

export const PaymentSolaControlPlaneSchema = z.object({
  enabled: z.boolean(),
  donationsEnabled: z.boolean(),
  publicIfieldsKey: z.string().min(1).nullable(),
  primaryDonationFundingMethod: DonationFundingMethodSchema,
  fallbackDonationFundingMethod: DonationFundingMethodSchema,
  terminalEnabled: z.boolean(),
}).strict();
export type PaymentSolaControlPlane = z.infer<typeof PaymentSolaControlPlaneSchema>;

export const DEFAULT_PAYMENT_SOLA_CONTROL_PLANE: PaymentSolaControlPlane = {
  enabled: true,
  donationsEnabled: true,
  publicIfieldsKey: null,
  primaryDonationFundingMethod: 'sola_daf',
  fallbackDonationFundingMethod: 'stripe_card',
  terminalEnabled: false,
};

export const BanquestWebhookRegistrationModeSchema = z.enum([
  'api_managed',
  'manual',
]);
export type BanquestWebhookRegistrationMode =
  z.infer<typeof BanquestWebhookRegistrationModeSchema>;

export const BanquestTransactionRetrievalModeSchema = z.enum([
  'curated_import_only',
  'scoped_read_through',
  'broad_read_through',
]);
export type BanquestTransactionRetrievalMode =
  z.infer<typeof BanquestTransactionRetrievalModeSchema>;

export const PaymentBanquestControlPlaneSchema = z.object({
  enabled: z.boolean(),
  registrationMode: BanquestWebhookRegistrationModeSchema,
  transactionRetrievalMode: BanquestTransactionRetrievalModeSchema,
  webhookId: z.number().int().positive().nullable(),
  webhookPathKey: z.string().min(1).nullable(),
  webhookActive: z.boolean(),
  lastWebhookSyncAt: UnixTs.nullable(),
  allowedImportModules: z.array(PaymentModuleIdSchema),
}).strict();
export type PaymentBanquestControlPlane =
  z.infer<typeof PaymentBanquestControlPlaneSchema>;

export const DEFAULT_PAYMENT_BANQUEST_CONTROL_PLANE: PaymentBanquestControlPlane = {
  enabled: false,
  registrationMode: 'manual',
  transactionRetrievalMode: 'curated_import_only',
  webhookId: null,
  webhookPathKey: null,
  webhookActive: false,
  lastWebhookSyncAt: null,
  allowedImportModules: [...PaymentModuleIdSchema.options],
};

export const PaymentDonationsFundingControlPlaneSchema = z.object({
  oneTimePrimaryFundingMethod: DonationFundingMethodSchema,
  oneTimeFallbackFundingMethod: DonationFundingMethodSchema,
  recurringFundingMethod: DonationFundingMethodSchema,
}).strict();
export type PaymentDonationsFundingControlPlane =
  z.infer<typeof PaymentDonationsFundingControlPlaneSchema>;

export const DEFAULT_PAYMENT_DONATIONS_FUNDING_CONTROL_PLANE: PaymentDonationsFundingControlPlane = {
  oneTimePrimaryFundingMethod: 'sola_daf',
  oneTimeFallbackFundingMethod: 'stripe_card',
  recurringFundingMethod: 'stripe_card',
};

export const PublicTuitionStripePaymentMethodTypeSchema = z.enum([
  'card',
  'us_bank_account',
  'cashapp',
  'klarna',
]);
export type PublicTuitionStripePaymentMethodType =
  z.infer<typeof PublicTuitionStripePaymentMethodTypeSchema>;

export const PaymentPublicTuitionPaymentMethodPolicySchema = z.object({
  automaticPaymentMethodsEnabled: z.boolean(),
  allowedPaymentMethodTypes: z.array(PublicTuitionStripePaymentMethodTypeSchema).min(1),
}).strict();
export type PaymentPublicTuitionPaymentMethodPolicy =
  z.infer<typeof PaymentPublicTuitionPaymentMethodPolicySchema>;

export const DEFAULT_PAYMENT_PUBLIC_TUITION_PAYMENT_METHOD_POLICY:
  PaymentPublicTuitionPaymentMethodPolicy = {
    automaticPaymentMethodsEnabled: true,
    allowedPaymentMethodTypes: ['card'],
  };

export const PaymentPublicTuitionPaymentMethodPolicyStoredSchema = z.object({
  automaticPaymentMethodsEnabled: z.boolean().nullable(),
  allowedPaymentMethodTypes: z.array(PublicTuitionStripePaymentMethodTypeSchema).nullable(),
}).strict();
export type PaymentPublicTuitionPaymentMethodPolicyStored =
  z.infer<typeof PaymentPublicTuitionPaymentMethodPolicyStoredSchema>;

export const PaymentPublicTuitionLaunchControlPlaneSchema = z.object({
  savedMethodsEnabled: z.boolean(),
  autoPayEnabled: z.boolean(),
  autoPaySweepKillSwitch: z.boolean(),
  webhookReconciliationReady: z.boolean(),
  transactionalEmailReady: z.boolean(),
  launchQaApproved: z.boolean(),
  paymentMethodPolicy: PaymentPublicTuitionPaymentMethodPolicySchema,
}).strict();
export type PaymentPublicTuitionLaunchControlPlane =
  z.infer<typeof PaymentPublicTuitionLaunchControlPlaneSchema>;

export const PaymentPublicTuitionLaunchControlPlaneStoredSchema = z.object({
  savedMethodsEnabled: z.boolean().nullable(),
  autoPayEnabled: z.boolean().nullable(),
  autoPaySweepKillSwitch: z.boolean().nullable(),
  webhookReconciliationReady: z.boolean().nullable(),
  transactionalEmailReady: z.boolean().nullable(),
  launchQaApproved: z.boolean().nullable(),
  paymentMethodPolicy: PaymentPublicTuitionPaymentMethodPolicyStoredSchema.default({
    automaticPaymentMethodsEnabled: null,
    allowedPaymentMethodTypes: null,
  }),
}).strict();
export type PaymentPublicTuitionLaunchControlPlaneStored =
  z.infer<typeof PaymentPublicTuitionLaunchControlPlaneStoredSchema>;

export const DEFAULT_PAYMENT_PUBLIC_TUITION_LAUNCH_CONTROL_PLANE: PaymentPublicTuitionLaunchControlPlane = {
  savedMethodsEnabled: false,
  autoPayEnabled: false,
  autoPaySweepKillSwitch: true,
  webhookReconciliationReady: false,
  transactionalEmailReady: false,
  launchQaApproved: false,
  paymentMethodPolicy: DEFAULT_PAYMENT_PUBLIC_TUITION_PAYMENT_METHOD_POLICY,
};

export const PaymentModuleOverrideSchema = z.object({
  moduleId: PaymentModuleIdSchema,
  stripeAccountId: z.string().min(1),
  chargeStrategy: PaymentChargeStrategySchema,
  hostedFlowKind: PaymentHostedFlowKindSchema,
  walletPolicy: PaymentWalletPolicySchema,
  enabled: z.boolean(),
  notes: z.string().min(1).optional(),
}).strict();
export type PaymentModuleOverride = z.infer<typeof PaymentModuleOverrideSchema>;

export const PaymentControlPlaneSchema = z.object({
  tenantId: OrgId,
  defaultStripeAccountId: z.string().min(1).nullable(),
  accountType: PaymentAccountTypeSchema,
  onboardingStatus: PaymentOnboardingStatusSchema,
  chargeStrategy: PaymentChargeStrategySchema,
  walletPolicy: PaymentWalletPolicySchema,
  centralWizardEnabled: z.boolean(),
  directChargeExceptionsAllowed: z.boolean(),
  hasStripeSecretConfigured: z.boolean(),
  hasWebhookSecretConfigured: z.boolean(),
  paymentsAuthority: PaymentsAuthoritySchema,
  topology: PaymentTopologySchema,
  webhookTopology: PaymentWebhookTopologySchema,
  moduleOverrides: z.array(PaymentModuleOverrideSchema),
  sola: PaymentSolaControlPlaneSchema.default(DEFAULT_PAYMENT_SOLA_CONTROL_PLANE),
  banquest: PaymentBanquestControlPlaneSchema.default(DEFAULT_PAYMENT_BANQUEST_CONTROL_PLANE),
  donationsFunding: PaymentDonationsFundingControlPlaneSchema.default(
    DEFAULT_PAYMENT_DONATIONS_FUNDING_CONTROL_PLANE
  ),
  publicTuition: PaymentPublicTuitionLaunchControlPlaneSchema.default(
    DEFAULT_PAYMENT_PUBLIC_TUITION_LAUNCH_CONTROL_PLANE
  ),
  publicTuitionStored: PaymentPublicTuitionLaunchControlPlaneStoredSchema.optional(),
  schoolPaymentMethodConfigurationCatalog: z.array(
    SchoolPaymentMethodConfigurationCatalogEntrySchema
  ),
  paymentMethodConfigurationAssignments: z.array(
    PaymentMethodConfigurationAssignmentSchema
  ),
  updatedAt: UnixTs,
  updatedBy: UserId.nullable().optional(),
}).strict();
export type PaymentControlPlane = z.infer<typeof PaymentControlPlaneSchema>;

export const PaymentStripeSecretModeSchema = z.enum(['test', 'live', 'unknown']);
export type PaymentStripeSecretMode = z.infer<typeof PaymentStripeSecretModeSchema>;

export const PaymentReadinessStatusSchema = z.enum(['not_ready', 'test_ready', 'live_ready']);
export type PaymentReadinessStatus = z.infer<typeof PaymentReadinessStatusSchema>;

export const PaymentReadinessIssueSeveritySchema = z.enum(['error', 'warning', 'info']);
export type PaymentReadinessIssueSeverity = z.infer<typeof PaymentReadinessIssueSeveritySchema>;

export const PaymentReadinessIssueSchema = z.object({
  code: z.string().min(1),
  severity: PaymentReadinessIssueSeveritySchema,
  message: z.string().min(1),
}).strict();
export type PaymentReadinessIssue = z.infer<typeof PaymentReadinessIssueSchema>;

export const PaymentControlPlaneReadinessControlPlaneSchema = z.object({
  onboardingStatus: PaymentOnboardingStatusSchema,
  chargeStrategy: PaymentChargeStrategySchema,
  defaultStripeAccountId: z.string().min(1).nullable(),
  accountType: PaymentAccountTypeSchema.nullable().optional(),
  hasModuleOverrides: z.boolean(),
  updatedAt: UnixTs,
  updatedBy: UserId.nullable().optional(),
}).strict();
export type PaymentControlPlaneReadinessControlPlane =
  z.infer<typeof PaymentControlPlaneReadinessControlPlaneSchema>;

export const PaymentControlPlaneReadinessRuntimeSchema = z.object({
  hasStripeSecretConfigured: z.boolean(),
  hasWebhookSecretConfigured: z.boolean(),
  stripeSecretMode: PaymentStripeSecretModeSchema,
  hasSuccessUrlConfigured: z.boolean(),
  hasCancelUrlConfigured: z.boolean(),
  hasPortalReturnUrlConfigured: z.boolean(),
  hasWebhookUrlConfigured: z.boolean(),
}).strict();
export type PaymentControlPlaneReadinessRuntime =
  z.infer<typeof PaymentControlPlaneReadinessRuntimeSchema>;

export const PaymentControlPlaneReadinessUrlsSchema = z.object({
  successUrl: z.string().url().nullable(),
  cancelUrl: z.string().url().nullable(),
  portalReturnUrl: z.string().url().nullable(),
  webhookUrl: z.string().url().nullable(),
  manifestUrl: z.string().url().nullable(),
  manifestPath: z.string().min(1),
}).strict();
export type PaymentControlPlaneReadinessUrls = z.infer<typeof PaymentControlPlaneReadinessUrlsSchema>;

export const PaymentControlPlaneReadinessStripeAccountSchema = z.object({
  accountId: z.string().min(1).nullable(),
  exists: z.boolean(),
  livemode: z.boolean().nullable(),
  chargesEnabled: z.boolean().nullable(),
  payoutsEnabled: z.boolean().nullable(),
  detailsSubmitted: z.boolean().nullable(),
  country: z.string().min(1).nullable(),
  email: z.string().min(1).nullable(),
  displayName: z.string().min(1).nullable(),
  accountType: z.string().min(1).nullable(),
  errorCode: z.string().min(1).nullable(),
  errorMessage: z.string().min(1).nullable(),
}).strict();
export type PaymentControlPlaneReadinessStripeAccount =
  z.infer<typeof PaymentControlPlaneReadinessStripeAccountSchema>;

export const PaymentControlPlaneReadinessSolaSchema = z.object({
  enabled: z.boolean(),
  donationsEnabled: z.boolean(),
  publicIfieldsKey: z.string().min(1).nullable(),
  hasPublicIfieldsKeyConfigured: z.boolean(),
  hasTransactionKeyConfigured: z.boolean(),
  hasWebhookConfigured: z.boolean(),
  primaryDonationFundingMethod: DonationFundingMethodSchema,
  fallbackDonationFundingMethod: DonationFundingMethodSchema,
  terminalEnabled: z.boolean(),
}).strict();
export type PaymentControlPlaneReadinessSola =
  z.infer<typeof PaymentControlPlaneReadinessSolaSchema>;

export const PaymentControlPlaneReadinessBanquestSchema = z.object({
  enabled: z.boolean(),
  registrationMode: BanquestWebhookRegistrationModeSchema,
  transactionRetrievalMode: BanquestTransactionRetrievalModeSchema,
  webhookId: z.number().int().positive().nullable(),
  webhookPathKey: z.string().min(1).nullable(),
  webhookActive: z.boolean(),
  lastWebhookSyncAt: UnixTs.nullable(),
  allowedImportModules: z.array(PaymentModuleIdSchema),
  hasSourceKeyConfigured: z.boolean(),
  hasSourceKeyPinConfigured: z.boolean(),
  hasApiCredentialConfigured: z.boolean(),
  hasWebhookSignatureConfigured: z.boolean(),
  environmentKind: z.enum(['sandbox', 'live']).nullable(),
  hasTokenizationPublicKeyConfigured: z.boolean(),
  tokenizationScriptUrl: z.string().url().nullable(),
  webhookUrl: z.string().url().nullable(),
  apiBaseUrl: z.string().url().nullable(),
}).strict();
export type PaymentControlPlaneReadinessBanquest =
  z.infer<typeof PaymentControlPlaneReadinessBanquestSchema>;

export const PaymentControlPlaneReadinessStripeHostedSchema = z.object({
  runtime: PaymentControlPlaneReadinessRuntimeSchema,
  account: PaymentControlPlaneReadinessStripeAccountSchema,
}).strict();
export type PaymentControlPlaneReadinessStripeHosted =
  z.infer<typeof PaymentControlPlaneReadinessStripeHostedSchema>;

export const PaymentControlPlaneReadinessSolaProviderSchema = z.object({
  enabled: z.boolean(),
  donationsEnabled: z.boolean(),
  publicIfieldsKey: z.string().min(1).nullable(),
  hasPublicIfieldsKeyConfigured: z.boolean(),
  hasTransactionKeyConfigured: z.boolean(),
  hasWebhookConfigured: z.boolean(),
  terminalEnabled: z.boolean(),
}).strict();
export type PaymentControlPlaneReadinessSolaProvider =
  z.infer<typeof PaymentControlPlaneReadinessSolaProviderSchema>;

export const PaymentControlPlaneReadinessDonationsFundingSchema = z.object({
  oneTimePrimaryFundingMethod: DonationFundingMethodSchema,
  oneTimeFallbackFundingMethod: DonationFundingMethodSchema,
  recurringFundingMethod: DonationFundingMethodSchema,
  oneTimeResolvedFundingMethod: DonationFundingMethodSchema.nullable(),
  recurringResolvedFundingMethod: DonationFundingMethodSchema.nullable(),
  availableFundingMethods: z.array(PaymentFundingMethodAvailabilityBaseSchema),
}).strict();
export type PaymentControlPlaneReadinessDonationsFunding =
  z.infer<typeof PaymentControlPlaneReadinessDonationsFundingSchema>;

export const PaymentControlPlaneReadinessPublicTuitionSchema =
  PaymentPublicTuitionLaunchControlPlaneSchema;
export type PaymentControlPlaneReadinessPublicTuition =
  z.infer<typeof PaymentControlPlaneReadinessPublicTuitionSchema>;
export const PaymentControlPlaneReadinessPublicTuitionStoredSchema =
  PaymentPublicTuitionLaunchControlPlaneStoredSchema;
export type PaymentControlPlaneReadinessPublicTuitionStored =
  z.infer<typeof PaymentControlPlaneReadinessPublicTuitionStoredSchema>;

export const PaymentControlPlaneReadinessLatestMirrorEventSchema = z.object({
  providerEventId: z.string().min(1).nullable(),
  eventType: z.string().min(1).nullable(),
  moduleTarget: PaymentModuleIdSchema.nullable(),
  occurredAt: UnixTs.nullable(),
  recordedAt: UnixTs.nullable(),
}).strict();
export type PaymentControlPlaneReadinessLatestMirrorEvent =
  z.infer<typeof PaymentControlPlaneReadinessLatestMirrorEventSchema>;

export const PaymentMethodConfigurationReadinessSchema = z.object({
  platformCatalog: z.array(PlatformPaymentMethodConfigurationCatalogEntrySchema),
  schoolCatalog: z.array(SchoolPaymentMethodConfigurationCatalogEntrySchema),
  effectiveCatalog: z.array(PaymentMethodConfigurationCatalogEntrySchema),
  effectiveAssignments: z.array(PaymentMethodConfigurationResolvedAssignmentSchema),
  issues: z.array(PaymentReadinessIssueSchema),
}).strict();
export type PaymentMethodConfigurationReadiness =
  z.infer<typeof PaymentMethodConfigurationReadinessSchema>;

export const PaymentControlPlaneReadinessSchema = z.object({
  tenantId: OrgId,
  status: PaymentReadinessStatusSchema,
  blockingIssueCount: z.number().int().nonnegative(),
  warningCount: z.number().int().nonnegative(),
  controlPlane: PaymentControlPlaneReadinessControlPlaneSchema,
  runtime: PaymentControlPlaneReadinessRuntimeSchema,
  urls: PaymentControlPlaneReadinessUrlsSchema,
  sola: PaymentControlPlaneReadinessSolaSchema,
  banquest: PaymentControlPlaneReadinessBanquestSchema,
  stripeHostedReadiness: PaymentControlPlaneReadinessStripeHostedSchema,
  solaProviderReadiness: PaymentControlPlaneReadinessSolaProviderSchema,
  donationsFundingReadiness: PaymentControlPlaneReadinessDonationsFundingSchema,
  publicTuition: PaymentControlPlaneReadinessPublicTuitionSchema.default(
    DEFAULT_PAYMENT_PUBLIC_TUITION_LAUNCH_CONTROL_PLANE
  ),
  publicTuitionStored: PaymentControlPlaneReadinessPublicTuitionStoredSchema.optional(),
  stripeAccount: PaymentControlPlaneReadinessStripeAccountSchema,
  latestMirrorEvent: PaymentControlPlaneReadinessLatestMirrorEventSchema,
  paymentMethodConfigurations: PaymentMethodConfigurationReadinessSchema,
  issues: z.array(PaymentReadinessIssueSchema),
}).strict();
export type PaymentControlPlaneReadiness = z.infer<typeof PaymentControlPlaneReadinessSchema>;

export const PaymentSolaControlPlaneUpdateInputSchema = z.object({
  enabled: z.boolean().optional(),
  donationsEnabled: z.boolean().optional(),
  publicIfieldsKey: z.string().min(1).nullable().optional(),
  primaryDonationFundingMethod: DonationFundingMethodSchema.optional(),
  fallbackDonationFundingMethod: DonationFundingMethodSchema.optional(),
  terminalEnabled: z.boolean().optional(),
}).strict();
export type PaymentSolaControlPlaneUpdateInput =
  z.infer<typeof PaymentSolaControlPlaneUpdateInputSchema>;

export const PaymentBanquestControlPlaneUpdateInputSchema = z.object({
  enabled: z.boolean().optional(),
  registrationMode: BanquestWebhookRegistrationModeSchema.optional(),
  transactionRetrievalMode: BanquestTransactionRetrievalModeSchema.optional(),
  webhookId: z.number().int().positive().nullable().optional(),
  webhookPathKey: z.string().min(1).nullable().optional(),
  webhookActive: z.boolean().optional(),
  lastWebhookSyncAt: UnixTs.nullable().optional(),
  allowedImportModules: z.array(PaymentModuleIdSchema).optional(),
}).strict();
export type PaymentBanquestControlPlaneUpdateInput =
  z.infer<typeof PaymentBanquestControlPlaneUpdateInputSchema>;

export const PaymentDonationsFundingControlPlaneUpdateInputSchema = z.object({
  oneTimePrimaryFundingMethod: DonationFundingMethodSchema.optional(),
  oneTimeFallbackFundingMethod: DonationFundingMethodSchema.optional(),
  recurringFundingMethod: DonationFundingMethodSchema.optional(),
}).strict();
export type PaymentDonationsFundingControlPlaneUpdateInput =
  z.infer<typeof PaymentDonationsFundingControlPlaneUpdateInputSchema>;

export const PaymentPublicTuitionLaunchControlPlaneUpdateInputSchema =
  PaymentPublicTuitionLaunchControlPlaneSchema.partial().strict();
export type PaymentPublicTuitionLaunchControlPlaneUpdateInput =
  z.infer<typeof PaymentPublicTuitionLaunchControlPlaneUpdateInputSchema>;

export const PaymentControlPlaneUpdateInputSchema = z.object({
  tenantId: OrgId.optional(),
  defaultStripeAccountId: z.string().min(1).nullable().optional(),
  accountType: PaymentAccountTypeSchema.optional(),
  onboardingStatus: PaymentOnboardingStatusSchema.optional(),
  chargeStrategy: PaymentChargeStrategySchema.optional(),
  walletPolicy: PaymentWalletPolicySchema.optional(),
  centralWizardEnabled: z.boolean().optional(),
  directChargeExceptionsAllowed: z.boolean().optional(),
  moduleOverrides: z.array(PaymentModuleOverrideSchema).optional(),
  sola: PaymentSolaControlPlaneUpdateInputSchema.optional(),
  banquest: PaymentBanquestControlPlaneUpdateInputSchema.optional(),
  donationsFunding: PaymentDonationsFundingControlPlaneUpdateInputSchema.optional(),
  publicTuition: PaymentPublicTuitionLaunchControlPlaneUpdateInputSchema.optional(),
  schoolPaymentMethodConfigurationCatalog: z.array(
    SchoolPaymentMethodConfigurationCatalogEntrySchema
  ).optional(),
  paymentMethodConfigurationAssignments: z.array(
    PaymentMethodConfigurationAssignmentSchema
  ).optional(),
}).strict();
export type PaymentControlPlaneUpdateInput = z.infer<typeof PaymentControlPlaneUpdateInputSchema>;

export const PaymentControlPlaneReadinessInputSchema = z.object({
  tenantId: OrgId.optional(),
}).strict();
export type PaymentControlPlaneReadinessInput = z.infer<typeof PaymentControlPlaneReadinessInputSchema>;

export const StripeConnectedAccountOnboardingActionSchema = z.enum(['created', 'linked']);
export type StripeConnectedAccountOnboardingAction =
  z.infer<typeof StripeConnectedAccountOnboardingActionSchema>;

export const StripeConnectedAccountBusinessTypeSchema = z.enum(['company', 'individual']);
export type StripeConnectedAccountBusinessType =
  z.infer<typeof StripeConnectedAccountBusinessTypeSchema>;

export const StartStripeConnectedAccountOnboardingInputSchema = z.object({
  tenantId: OrgId.optional(),
  existingStripeAccountId: z.string().min(1).nullable().optional(),
  contactEmail: z.string().email().nullable().optional(),
  country: z.string().regex(/^[A-Z]{2}$/).optional(),
  businessType: StripeConnectedAccountBusinessTypeSchema.optional(),
  refreshUrl: z.string().url(),
  returnUrl: z.string().url(),
}).strict();
export type StartStripeConnectedAccountOnboardingInput =
  z.infer<typeof StartStripeConnectedAccountOnboardingInputSchema>;

export const CompleteStripeConnectedAccountOnboardingInputSchema = z.object({
  tenantId: OrgId.optional(),
  stripeAccountId: z.string().min(1).optional(),
}).strict();
export type CompleteStripeConnectedAccountOnboardingInput =
  z.infer<typeof CompleteStripeConnectedAccountOnboardingInputSchema>;

export const PaymentFundingMethodAvailabilitySchema =
  PaymentFundingMethodAvailabilityBaseSchema;
export type PaymentFundingMethodAvailability =
  z.infer<typeof PaymentFundingMethodAvailabilitySchema>;

export const DonationFundingJourneyConfigSchema = z.object({
  primaryFundingMethod: DonationFundingMethodSchema,
  fallbackFundingMethod: DonationFundingMethodSchema.nullable(),
  resolvedFundingMethod: DonationFundingMethodSchema.nullable(),
}).strict();
export type DonationFundingJourneyConfig =
  z.infer<typeof DonationFundingJourneyConfigSchema>;

export const RecurringDonationFundingConfigSchema = z.object({
  fundingMethod: DonationFundingMethodSchema,
  resolvedFundingMethod: DonationFundingMethodSchema.nullable(),
}).strict();
export type RecurringDonationFundingConfig =
  z.infer<typeof RecurringDonationFundingConfigSchema>;

export const DonationFundingConfigSchema = z.object({
  tenantId: OrgId,
  moduleId: z.literal('donations'),
  primaryFundingMethod: DonationFundingMethodSchema,
  fallbackFundingMethod: DonationFundingMethodSchema,
  oneTime: DonationFundingJourneyConfigSchema,
  recurring: RecurringDonationFundingConfigSchema,
  availableFundingMethods: z.array(PaymentFundingMethodAvailabilitySchema),
  sola: z.object({
    enabled: z.boolean(),
    publicIfieldsKey: z.string().min(1).nullable(),
    terminalEnabled: z.boolean(),
  }).strict(),
  stripe: z.object({
    enabled: z.boolean(),
  }).strict(),
}).strict();
export type DonationFundingConfig = z.infer<typeof DonationFundingConfigSchema>;

export const StripeConnectedAccountOnboardingSessionSchema = z.object({
  action: StripeConnectedAccountOnboardingActionSchema,
  stripeAccountId: z.string().min(1),
  onboardingUrl: z.string().url(),
  expiresAt: UnixTs.nullable(),
  accountType: PaymentAccountTypeSchema,
  contactEmail: z.string().email().nullable(),
  country: z.string().regex(/^[A-Z]{2}$/).nullable(),
}).strict();
export type StripeConnectedAccountOnboardingSession =
  z.infer<typeof StripeConnectedAccountOnboardingSessionSchema>;

export const EnsureStripeCustomerInputSchema = z.object({
  tenantId: OrgId.optional(),
  moduleId: PaymentModuleIdSchema,
  holderId: z.string().min(1).optional(),
}).strict();
export type EnsureStripeCustomerInput = z.infer<typeof EnsureStripeCustomerInputSchema>;

export const EnsureStripeCustomerResponseSchema = z.object({
  customerId: z.string().min(1),
  stripeAccountId: z.string().min(1),
  moduleId: PaymentModuleIdSchema,
  chargeStrategy: PaymentChargeStrategySchema,
  holderId: z.string().min(1),
  holderType: PaymentHolderTypeSchema,
  provider: z.literal('stripe'),
}).strict();
export type EnsureStripeCustomerResponse = z.infer<typeof EnsureStripeCustomerResponseSchema>;

export const PaymentHolderSchema = z.object({
  id: z.string().min(1),
  holderType: PaymentHolderTypeSchema,
  entityId: z.string().min(1),
  displayName: z.string().min(1),
  status: PaymentHolderStatusSchema,
}).strict();
export type PaymentHolder = z.infer<typeof PaymentHolderSchema>;

export const PaymentManagementContextInputSchema = z.object({
  tenantId: OrgId.optional(),
  moduleId: PaymentModuleIdSchema.optional(),
}).strict();
export type PaymentManagementContextInput = z.infer<typeof PaymentManagementContextInputSchema>;

export const PaymentManagementContextSchema = z.object({
  tenantId: OrgId,
  familyId: z.string().min(1).nullable(),
  defaultHolderId: z.string().min(1).nullable(),
  defaultHolderType: PaymentHolderTypeSchema.nullable(),
  managedHolders: z.array(PaymentHolderSchema),
  linkStatus: PaymentManagementLinkStatusSchema,
  claimFamilyId: z.string().min(1).nullable().optional(),
  claimStatus: PaymentManagementClaimStatusSchema,
}).strict();
export type PaymentManagementContext = z.infer<typeof PaymentManagementContextSchema>;

export const HostedPaymentLineItemSchema = z.object({
  priceId: z.string().min(1).optional(),
  unitAmountCents: z.number().int().positive().optional(),
  quantity: z.number().int().positive(),
  name: z.string().min(1).optional(),
  description: z.string().min(1).optional(),
  recurringInterval: PaymentRecurringIntervalSchema.optional(),
  recurringIntervalCount: z.number().int().positive().optional(),
}).strict();
export type HostedPaymentLineItem = z.infer<typeof HostedPaymentLineItemSchema>;

export const HostedPaymentSessionInputSchema = z.object({
  tenantId: OrgId.optional(),
  moduleId: PaymentModuleIdSchema,
  operationKey: PaymentCheckoutOperationKeySchema,
  holderId: z.string().min(1).optional(),
  mode: PaymentCheckoutModeSchema.optional(),
  currency: z.string().trim().length(3).optional(),
  successUrl: z.string().url().optional(),
  cancelUrl: z.string().url().optional(),
  lineItems: z.array(HostedPaymentLineItemSchema).min(1),
  customerEmail: z.string().email().optional(),
  customerId: z.string().min(1).optional(),
  customerReferenceId: z.string().min(1).optional(),
  accountOverrideId: z.string().min(1).optional(),
  applicationFeeAmountCents: z.number().int().nonnegative().optional(),
  description: z.string().min(1).optional(),
  metadata: z.record(z.string(), z.string()).optional(),
}).strict();
export type HostedPaymentSessionInput = z.infer<typeof HostedPaymentSessionInputSchema>;

export const HostedPaymentSessionResponseSchema = z.object({
  sessionId: z.string().min(1),
  url: z.string().url(),
  moduleId: PaymentModuleIdSchema,
  mode: PaymentCheckoutModeSchema,
  chargeStrategy: PaymentChargeStrategySchema,
  hostedFlowKind: PaymentHostedFlowKindSchema,
  stripeAccountId: z.string().min(1),
  provider: z.literal('stripe'),
}).strict();
export type HostedPaymentSessionResponse = z.infer<typeof HostedPaymentSessionResponseSchema>;

export const HostedPortalSessionInputSchema = z.object({
  tenantId: OrgId.optional(),
  moduleId: PaymentModuleIdSchema,
  holderId: z.string().min(1).optional(),
  customerId: z.string().min(1).optional(),
  returnUrl: z.string().url().optional(),
  accountOverrideId: z.string().min(1).optional(),
}).strict();
export type HostedPortalSessionInput = z.infer<typeof HostedPortalSessionInputSchema>;

export const HostedPortalSessionResponseSchema = z.object({
  sessionId: z.string().min(1),
  url: z.string().url(),
  moduleId: PaymentModuleIdSchema,
  hostedFlowKind: z.literal('billing-portal'),
  stripeAccountId: z.string().min(1),
  provider: z.literal('stripe'),
}).strict();
export type HostedPortalSessionResponse = z.infer<typeof HostedPortalSessionResponseSchema>;

export const CreateDonationDafRecommendationInputSchema = z.object({
  tenantId: OrgId.optional(),
  campaignId: z.string().uuid(),
  grossCents: z.number().int().positive(),
  feeRecoveryCents: z.number().int().nonnegative().optional(),
  exp: z.string().regex(/^\d{4}$/),
  cardToken: z.string().min(1),
  cvvToken: z.string().min(1),
  donorFirstName: z.string().min(1),
  donorLastName: z.string().min(1),
  donorEmail: z.string().email(),
  donorPhone: z.string().nullable().optional(),
  dedicationType: z.enum(['HONOR', 'MEMORY']).nullable().optional(),
  honoreeName: z.string().nullable().optional(),
  dedicationMessage: z.string().nullable().optional(),
  isAnonymous: z.boolean().optional(),
  publicDisplayName: z.string().nullable().optional(),
  route: z.string().min(1).optional(),
}).strict();
export type CreateDonationDafRecommendationInput =
  z.infer<typeof CreateDonationDafRecommendationInputSchema>;

export const DonationDafRecommendationResponseSchema = z.object({
  donationId: z.string().uuid(),
  status: z.string().min(1),
  provider: z.literal('sola'),
  fundingMethod: z.literal('sola_daf'),
  providerTransactionRef: z.string().min(1),
  providerResponseResult: z.string().nullable().optional(),
  providerMessage: z.string().nullable().optional(),
}).strict();
export type DonationDafRecommendationResponse =
  z.infer<typeof DonationDafRecommendationResponseSchema>;

export const VoidDonationDafRecommendationInputSchema = z.object({
  tenantId: OrgId.optional(),
  donationId: z.string().uuid(),
}).strict();
export type VoidDonationDafRecommendationInput =
  z.infer<typeof VoidDonationDafRecommendationInputSchema>;

export const VoidDonationDafRecommendationResponseSchema = z.object({
  donationId: z.string().uuid(),
  status: z.string().min(1),
  provider: z.literal('sola'),
  providerTransactionRef: z.string().min(1),
}).strict();
export type VoidDonationDafRecommendationResponse =
  z.infer<typeof VoidDonationDafRecommendationResponseSchema>;

export const PendingDonationSettlementItemSchema = z.object({
  donationId: z.string().uuid(),
  tenantId: OrgId,
  campaignId: z.string().uuid(),
  campaignName: z.string().min(1),
  donorDisplayName: z.string().min(1).nullable(),
  grossCents: z.number().int().nonnegative(),
  feeRecoveryCents: z.number().int().nonnegative(),
  status: z.string().min(1),
  fundingMethod: DonationFundingMethodSchema,
  provider: PaymentProviderSchema,
  providerTransactionRef: z.string().min(1).nullable(),
  settlementState: DonationSettlementStateSchema,
  settlementAuthority: DonationSettlementAuthoritySchema,
  submittedAt: UnixTs.nullable(),
  updatedAt: UnixTs,
}).strict();
export type PendingDonationSettlementItem =
  z.infer<typeof PendingDonationSettlementItemSchema>;

export const PendingDonationSettlementListResponseSchema = z.object({
  items: z.array(PendingDonationSettlementItemSchema),
  totalCount: z.number().int().nonnegative(),
}).strict();
export type PendingDonationSettlementListResponse =
  z.infer<typeof PendingDonationSettlementListResponseSchema>;

export const ApplyDonationSettlementObservationInputSchema = z.object({
  tenantId: OrgId.optional(),
  donationId: z.string().uuid(),
  settlementState: DonationSettlementStateSchema,
  settlementAuthority: DonationSettlementAuthoritySchema.optional(),
  observedAt: UnixTs.optional(),
  providerTransactionRef: z.string().min(1).nullable().optional(),
  providerEventRef: z.string().min(1).nullable().optional(),
  note: z.string().min(1).nullable().optional(),
}).strict();
export type ApplyDonationSettlementObservationInput =
  z.infer<typeof ApplyDonationSettlementObservationInputSchema>;

export const ApplyDonationSettlementObservationResponseSchema = z.object({
  donationId: z.string().uuid(),
  status: z.string().min(1),
  settlementState: DonationSettlementStateSchema,
  settlementAuthority: DonationSettlementAuthoritySchema,
  provider: PaymentProviderSchema,
  providerTransactionRef: z.string().min(1).nullable(),
  providerEventRef: z.string().min(1).nullable(),
  observedAt: UnixTs,
}).strict();
export type ApplyDonationSettlementObservationResponse =
  z.infer<typeof ApplyDonationSettlementObservationResponseSchema>;

export const PaymentEventEnvelopeSchema = z.object({
  tenantId: OrgId,
  provider: PaymentProviderSchema,
  providerEventId: z.string().min(1),
  ingestSource: PaymentEventIngestSourceSchema,
  providerAccountId: z.string().min(1).nullable().optional(),
  providerTransactionId: z.string().min(1).nullable().optional(),
  stripeAccountId: z.string().min(1).nullable().optional(),
  moduleTarget: PaymentModuleIdSchema,
  eventType: z.string().min(1),
  objectType: z.string().min(1),
  idempotencyKey: z.string().min(1),
  occurredAt: UnixTs,
  payload: z.record(z.string(), z.unknown()).optional(),
}).strict();
export type PaymentEventEnvelope = z.infer<typeof PaymentEventEnvelopeSchema>;

export const PaymentMirrorLedgerEntrySchema = PaymentEventEnvelopeSchema.extend({
  id: z.string().uuid(),
  recordedAt: UnixTs,
}).strict();
export type PaymentMirrorLedgerEntry = z.infer<typeof PaymentMirrorLedgerEntrySchema>;

export const PaymentMirrorLedgerQuerySchema = z.object({
  tenantId: OrgId.optional(),
  moduleId: PaymentModuleIdSchema.optional(),
  limit: z.number().int().positive().max(100).optional(),
  nextToken: z.string().min(1).optional(),
}).strict();
export type PaymentMirrorLedgerQuery = z.infer<typeof PaymentMirrorLedgerQuerySchema>;

export const PaymentMirrorLedgerListResponseSchema = z.object({
  items: z.array(PaymentMirrorLedgerEntrySchema),
  nextToken: z.string().nullable().optional(),
  totalCount: z.number().int().nonnegative(),
}).strict();
export type PaymentMirrorLedgerListResponse = z.infer<typeof PaymentMirrorLedgerListResponseSchema>;

export const ProviderTransactionDiscoverySourceSchema = z.enum([
  'provider_webhook',
  'provider_search',
  'operator_import',
  'operator_refresh',
]);
export type ProviderTransactionDiscoverySource =
  z.infer<typeof ProviderTransactionDiscoverySourceSchema>;

export const ProviderTransactionCorrelationStatusSchema = z.enum([
  'platform_correlated',
  'external_unclaimed',
  'module_claimed',
]);
export type ProviderTransactionCorrelationStatus =
  z.infer<typeof ProviderTransactionCorrelationStatusSchema>;

export const ProviderTransactionObservationSchema = z.object({
  id: z.string().min(1),
  tenantId: OrgId,
  provider: PaymentProviderSchema,
  providerAccountId: z.string().min(1).nullable(),
  providerTransactionId: z.string().min(1),
  providerEventId: z.string().min(1).nullable(),
  discoverySource: ProviderTransactionDiscoverySourceSchema,
  correlationStatus: ProviderTransactionCorrelationStatusSchema,
  linkedModuleTarget: PaymentModuleIdSchema.nullable(),
  linkedRecordId: z.string().min(1).nullable(),
  eventType: z.string().min(1).nullable(),
  objectType: z.string().min(1).nullable(),
  status: z.string().min(1).nullable(),
  paymentType: z.string().min(1).nullable(),
  transactionKind: z.string().min(1).nullable(),
  occurredAt: UnixTs.nullable(),
  settledAt: UnixTs.nullable(),
  amountCents: z.number().int().nonnegative().nullable(),
  currency: z.string().trim().min(1).nullable(),
  payload: z.record(z.string(), z.unknown()),
  createdAt: UnixTs,
  updatedAt: UnixTs,
}).strict();
export type ProviderTransactionObservation =
  z.infer<typeof ProviderTransactionObservationSchema>;

export const ProviderTransactionObservationListResponseSchema = z.object({
  items: z.array(ProviderTransactionObservationSchema),
  totalCount: z.number().int().nonnegative(),
}).strict();
export type ProviderTransactionObservationListResponse =
  z.infer<typeof ProviderTransactionObservationListResponseSchema>;

export const BanquestTransactionStatusFilterSchema = z.enum([
  'captured',
  'pending',
  'reserve',
  'originated',
  'returned',
  'cancelled',
  'queued',
  'declined',
  'error',
  'settled',
  'voided',
  'approved',
  'blocked',
  'expired',
]);
export type BanquestTransactionStatusFilter =
  z.infer<typeof BanquestTransactionStatusFilterSchema>;

export const BanquestTransactionPaymentTypeFilterSchema = z.enum([
  'credit_card',
  'check',
]);
export type BanquestTransactionPaymentTypeFilter =
  z.infer<typeof BanquestTransactionPaymentTypeFilterSchema>;

export const BanquestTransactionDateFieldSchema = z.enum([
  'settled_at',
  'created_at',
]);
export type BanquestTransactionDateField =
  z.infer<typeof BanquestTransactionDateFieldSchema>;

const ProviderDateFilterValueSchema = z.union([
  z.number().int().nonnegative(),
  z.string().datetime(),
]);

export const SearchBanquestTransactionsInputSchema = z.object({
  tenantId: OrgId.optional(),
  status: z.array(BanquestTransactionStatusFilterSchema).optional(),
  paymentType: z.array(BanquestTransactionPaymentTypeFilterSchema).optional(),
  settledDate: z.string().date().optional(),
  dateField: BanquestTransactionDateFieldSchema.optional(),
  dateFrom: ProviderDateFilterValueSchema.optional(),
  dateTo: ProviderDateFilterValueSchema.optional(),
  limit: z.number().int().positive().max(100).optional(),
  offset: z.number().int().nonnegative().optional(),
  key: z.string().min(1).optional(),
}).strict();
export type SearchBanquestTransactionsInput =
  z.infer<typeof SearchBanquestTransactionsInputSchema>;

export const GetBanquestTransactionInputSchema = z.object({
  tenantId: OrgId.optional(),
  transactionId: z.number().int().positive(),
}).strict();
export type GetBanquestTransactionInput = z.infer<typeof GetBanquestTransactionInputSchema>;

export const ImportBanquestTransactionObservationInputSchema = z.object({
  tenantId: OrgId.optional(),
  transactionId: z.number().int().positive(),
}).strict();
export type ImportBanquestTransactionObservationInput =
  z.infer<typeof ImportBanquestTransactionObservationInputSchema>;

export const ClaimImportedBanquestTransactionInputSchema = z.object({
  tenantId: OrgId.optional(),
  providerTransactionId: z.string().min(1),
  moduleTarget: PaymentModuleIdSchema,
  linkedRecordId: z.string().min(1),
}).strict();
export type ClaimImportedBanquestTransactionInput =
  z.infer<typeof ClaimImportedBanquestTransactionInputSchema>;

export const RefreshImportedBanquestTransactionInputSchema = z.object({
  tenantId: OrgId.optional(),
  providerTransactionId: z.string().min(1),
}).strict();
export type RefreshImportedBanquestTransactionInput =
  z.infer<typeof RefreshImportedBanquestTransactionInputSchema>;

export const BanquestWebhookSchema = z.object({
  id: z.number().int().positive(),
  webhookUrl: z.string().url(),
  description: z.string().min(1).nullable(),
  active: z.boolean(),
  signature: z.string().min(1).nullable(),
}).strict();
export type BanquestWebhook = z.infer<typeof BanquestWebhookSchema>;

export const BanquestWebhookListResponseSchema = z.object({
  items: z.array(BanquestWebhookSchema),
  totalCount: z.number().int().nonnegative(),
}).strict();
export type BanquestWebhookListResponse =
  z.infer<typeof BanquestWebhookListResponseSchema>;

export const CreateBanquestWebhookInputSchema = z.object({
  tenantId: OrgId.optional(),
  webhookUrl: z.string().url(),
  description: z.string().min(1).nullable().optional(),
  active: z.boolean().optional(),
}).strict();
export type CreateBanquestWebhookInput = z.infer<typeof CreateBanquestWebhookInputSchema>;

export const UpdateBanquestWebhookInputSchema = z.object({
  tenantId: OrgId.optional(),
  webhookId: z.number().int().positive(),
  webhookUrl: z.string().url().optional(),
  description: z.string().min(1).nullable().optional(),
  active: z.boolean().optional(),
}).strict();
export type UpdateBanquestWebhookInput = z.infer<typeof UpdateBanquestWebhookInputSchema>;

export const DeleteBanquestWebhookInputSchema = z.object({
  tenantId: OrgId.optional(),
  webhookId: z.number().int().positive(),
}).strict();
export type DeleteBanquestWebhookInput = z.infer<typeof DeleteBanquestWebhookInputSchema>;

export const SyncBanquestWebhookStateInputSchema = z.object({
  tenantId: OrgId.optional(),
}).strict();
export type SyncBanquestWebhookStateInput = z.infer<typeof SyncBanquestWebhookStateInputSchema>;
